/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { useQuery } from '@apollo/client';

import { phoenixShopHotelAmenities } from '../../HotelGalleryTitle/HQVQueries';
import { isServer } from '@marriott/mi-apollo-client-utils';
import { AmenitiesAPIData, AmenityProps, KeyAmenityProps, KeyAmenityFromAEMProps } from '../HotelInformation.types';
import { Styled } from './index.styles';
import { useCheckBreakpoint } from '../../../hooks';
import { useConditionalPageContextSERP, useConditionalServerDataHotelInformationSERP } from '../../../hooks';
import { hotelInformationMockData } from '../mock/hotelInformation';
import {
  complimentaryServicesCategoryCodes,
  beautyConfig,
  breakfastCodes,
  complimentaryServicesConfigList,
  selectServiceFeatureSortList,
  fullServiceFeatureSortList,
  brandConfig,
} from '../constants/ApplicationConstants';
import { logger } from '../../../utils';
import { useConditionalHQVContext } from '../../../hooks/lib/useConditionalHQVContext';
import { useHQVDatalayerTrackingStore } from '../../../modules/store/hqvDatalayerTrackingStore';
import { canUseDOM } from '@marriott/mi-ui-library';

const { log } = logger({})('HotelGalleryInfoWrapper/Amenities');

const AMENITIES_CACHE_PREFIX = 'hqv-amenities-';

function getCachedAmenities(propertyId: string): any | null {
  if (!canUseDOM) return null;
  try {
    const raw = sessionStorage.getItem(`${AMENITIES_CACHE_PREFIX}${propertyId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setCachedAmenities(propertyId: string, data: any): void {
  if (!canUseDOM) return;
  try {
    sessionStorage.setItem(`${AMENITIES_CACHE_PREFIX}${propertyId}`, JSON.stringify(data));
  } catch {
    // sessionStorage full or unavailable
  }
}

export const Amenities: React.FC<AmenitiesAPIData> = () => {
  // log.debug('HotelAmenitiesComponent render');
  const { jsonContent, brandCode } = useConditionalServerDataHotelInformationSERP();
  const { requestId, pageState, currentLocale, resolvedUrl, isAuthorMode, pageVal } = useConditionalPageContextSERP();
  const { hqvState } = useConditionalHQVContext();
  const propertyId = hqvState.propertyIDvalue;
  const modalOpenState = hqvState.modalOpenState;
  const hideBorder = resolvedUrl?.includes('/availabilityCalendar') ? true : false;
  const reqId = useMemo(() => {
    return requestId;
  }, [requestId]);

  // HQV Datalayer tracking store - track amenities call status
  const setAmenitiesCallStatus = useHQVDatalayerTrackingStore(state => state.setAmenitiesCallStatus);

  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportM = useCheckBreakpoint('viewportM');
  const isViewportS = useCheckBreakpoint('viewportS');

  // Memoize all variables that affect the query,
  // to prevent re-triggering useQuery if component re-renders.
  const skipQuery = useMemo(() => {
    // Skip query if:
    // 1. In author mode
    // 2. propertyId is not available (empty, undefined, or null)
    return !!pageState?.authorMode || !!isAuthorMode || !propertyId;
  }, [pageState, isAuthorMode, propertyId]);

  const {
    // loading: amenitiesDataGraphLoading,
    error: _amenitiesDataGraphError,
    data: amenitiesDataGraph,
  } = useQuery(phoenixShopHotelAmenities, {
    fetchPolicy: isServer ? 'network-only' : 'cache-first',
    variables: {
      propertyId: propertyId,
    },
    context: {
      headers: {
        // Any header, including x-request-id, can be passed in args with query.
        // If you don't pass it in the authLink will generate a random ID.
        'x-request-id': reqId,
        'accept-language': currentLocale,
      },
    },
    skip: skipQuery,
    onCompleted: data => {
      log.debug('phoenixShopHotelAmenities complete: ', data);
      setAmenitiesCallStatus('success');
      if (propertyId && data?.property?.facilitiesAndServices) {
        setCachedAmenities(propertyId, data);
      }
    },
    onError: error => {
      log.debug('phoenixShopHotelAmenities failed: ', error);
      // Note: Don't set error status here - we'll check for cached data below
      // Apollo's cache-first policy may still have data available
    },
  });

  // Fall back to sessionStorage when Apollo has no usable data (e.g., after page refresh with blocked call)
  const cachedAmenities = useMemo(() => {
    if (!amenitiesDataGraph?.property?.facilitiesAndServices && _amenitiesDataGraphError && propertyId) {
      return getCachedAmenities(propertyId);
    }
    return null;
  }, [amenitiesDataGraph, _amenitiesDataGraphError, propertyId]);

  const resolvedAmenitiesData = amenitiesDataGraph || cachedAmenities;

  // Set amenities call status based on data availability (cache resilience)
  // If data exists (fresh, Apollo-cached, or sessionStorage-cached), consider it a success
  // Only set error if there's an error AND no data available from any source
  useEffect(() => {
    if (resolvedAmenitiesData?.property?.facilitiesAndServices) {
      log.debug('Amenities data available (fresh or cached), setting status to success');
      setAmenitiesCallStatus('success');
    } else if (_amenitiesDataGraphError && !resolvedAmenitiesData?.property?.facilitiesAndServices) {
      log.debug('Amenities call failed with no cached data, setting status to error');
      setAmenitiesCallStatus('error');
    }
  }, [resolvedAmenitiesData, _amenitiesDataGraphError, setAmenitiesCallStatus]);

  log.debug('amenitiesDataGraph: ', amenitiesDataGraph);

  const amenitiesData = useMemo(() => {
    return pageState?.authorMode || isAuthorMode ? hotelInformationMockData.amenitiesData : resolvedAmenitiesData;
  }, [pageState?.authorMode, resolvedAmenitiesData, isAuthorMode]);
  const tempAmenities = useMemo(() => {
    return amenitiesData?.property?.facilitiesAndServices;
  }, [amenitiesData]);

  const healthAndServices: string[] = useMemo(() => {
    const tempHealthAndServices: string[] = [];
    tempAmenities?.forEach((amenity: AmenityProps) => {
      if (
        amenity?.type?.code === 'Guest Facilities' ||
        amenity?.type?.code === 'Guest Services' ||
        amenity?.type?.code === 'Shopping & Stores'
      ) {
        if (beautyConfig.includes(amenity?.categoryType?.code) && amenity?.details) {
          tempHealthAndServices.push(amenity.details[0].key);
        }
      }
    });
    return tempHealthAndServices;
  }, [tempAmenities]);

  const servicesList: string[] = useMemo(() => {
    const tempSercvicesList: string[] = [];
    tempAmenities?.forEach((amenity: AmenityProps) => {
      if (
        amenity?.type?.code === 'Guest Facilities' ||
        amenity?.type?.code === 'Guest Services' ||
        amenity?.type?.code === 'Shopping & Stores'
      ) {
        if (beautyConfig.includes(amenity?.categoryType?.code) && amenity?.details) {
          healthAndServices.push(amenity.details[0].key);
        } else if (breakfastCodes.includes(amenity?.categoryType?.code)) {
          const value = `${amenity?.description}: ${amenity.details[0]?.key}`;
          tempSercvicesList.push(value);
        } else {
          tempSercvicesList.push(amenity?.description);
        }
      } else if (
        amenity?.type?.code &&
        complimentaryServicesConfigList.includes(amenity.type.code) &&
        complimentaryServicesCategoryCodes.includes(amenity?.categoryType?.code)
      ) {
        tempSercvicesList.push(amenity.description);
      }
    });

    return tempSercvicesList;
  }, [healthAndServices, tempAmenities]);

  let lookup: KeyAmenityFromAEMProps[];
  if (brandConfig.includes(brandCode)) {
    lookup = selectServiceFeatureSortList;
  } else {
    lookup = fullServiceFeatureSortList;
  }

  const allAmenities: string[] = useMemo(() => {
    const amenListTemp: string[] = [];
    let idx = 0;
    while (idx < servicesList.length) {
      if (!amenListTemp.includes(servicesList[idx])) {
        amenListTemp.push(servicesList[idx]);
      }
      idx += 1;
    }
    return amenListTemp;
  }, [servicesList]);

  const validKeyAmenitiesList: string[] = useMemo(() => {
    const keyAmenList: string[] = [];
    lookup.forEach((amenity: KeyAmenityFromAEMProps) => {
      keyAmenList.push(amenity['amenitiesTagId']);
    });
    return keyAmenList;
  }, [lookup]);

  const tempKeyAmenities: KeyAmenityProps[] = amenitiesData?.property.matchingSearchFacets;

  // generate memoized keyAmentiesList based on tempKeyAmenities
  const keyAmenitiesList: KeyAmenityProps[] = useMemo(() => {
    const keyAmenTempList: KeyAmenityProps[] = [];
    tempKeyAmenities?.forEach((keyAmenity: KeyAmenityProps) => {
      if (validKeyAmenitiesList.includes(keyAmenity?.dimension?.code)) {
        keyAmenTempList.push(keyAmenity);
      }
    });
    return keyAmenTempList;
  }, [tempKeyAmenities, validKeyAmenitiesList]);

  const [seeMore, setSeeMore] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const seeMoreButton = useRef(null);
  const seeFewerButton = useRef(null);
  // const { hqvDispatch } = useHQVContext();
  const clickTrackingLoc = 'HQV AEM Page';
  //The values 9 and 6 are based on the Visual Designs provided.
  const desktopCount = 9;
  const mobileCount = 6;
  let amenitiesCountLimit = desktopCount;
  if (isViewportS && !isViewportM) {
    amenitiesCountLimit = mobileCount;
  }

  function formatIconName(icon: string): string {
    if (!icon) {
      return 'icon-check';
    }
    const formattedIcon = 'icon-' + icon;
    return 'icon-check ' + formattedIcon;
  }

  const amenitiesList: { icon: string; id: string; name: string }[] = useMemo(() => {
    const createdAmenitiesList = [];
    if (keyAmenitiesList) {
      for (let i = 0; i < keyAmenitiesList?.length; i++) {
        let icon = '';
        if (keyAmenitiesList[i]?.dimension?.code) {
          icon = formatIconName(keyAmenitiesList[i]?.dimension?.code);
        }
        const id = keyAmenitiesList[i]?.dimension?.code;
        const name = keyAmenitiesList[i]?.dimension?.description;
        createdAmenitiesList.push({ icon: icon, id: id, name: name });
      }
    }

    if (allAmenities) {
      for (let i = 0; i < allAmenities?.length; i++) {
        const icon = formatIconName('');
        const id = '';
        const name = allAmenities[i];
        createdAmenitiesList.push({ icon: icon, id: id, name: name });
      }
    }

    return createdAmenitiesList;
  }, [keyAmenitiesList, allAmenities]);

  const showMoreFlag = useMemo(() => {
    return amenitiesList.length > amenitiesCountLimit;
  }, [amenitiesList, amenitiesCountLimit]);

  useEffect(() => {
    if (hasBeenClicked) {
      const focusOptions = pageVal === 'chat' ? { preventScroll: true } : undefined;
      if (seeMore) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((seeFewerButton as any).current) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (seeFewerButton as any).current.focus(focusOptions);
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((seeMoreButton as any).current) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (seeMoreButton as any).current.focus(focusOptions);
        }
      }
    }
  }, [hasBeenClicked, seeMore, pageVal]);

  // eslint-disable-next-line no-extra-boolean-cast
  return amenitiesList && amenitiesList.length > 0 ? (
    <Styled>
      <div
        id="hqv-amenities-section"
        className={clsx(
          pageVal === 'chat' && !isViewportL ? 'px-4 pt-5 m-0' : '',
          'amenities-component',
          'space-buffer',
          'adjustable-margin-element',
          `${modalOpenState ? 'hqv-modal' : ''}`,
          'pb-5'
        )}
      >
        <h2
          className={clsx(
            pageVal === 'chat' ? 't-subtitle-m' : isViewportL ? 't-title-s' : 't-subtitle-xl',
            'component-heading',
            pageVal === 'chat' && !isViewportL ? 'mb-3' : ''
          )}
        >
          {jsonContent?.amenitiesLabel}
        </h2>
        <div
          className={clsx(`amenities-content ${hideBorder ? 'remove-border' : ''}`, pageVal === 'chat' ? 'pb-5' : '')}
        >
          <div className="component-content">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {amenitiesList?.slice(0, amenitiesCountLimit).map((amenity: any, index: number) => (
              <div
                className={clsx(
                  pageVal === 'chat' ? 'align-items-center' : '',
                  'col',
                  'm-icon-text',
                  'amenity-list-item'
                )}
                key={index}
              >
                <span
                  className={clsx(pageVal === 'chat' ? 'icon-s' : 'icon-m icon-decorative icon-holder', amenity.icon)}
                ></span>
                <span className={clsx(pageVal === 'chat' ? 't-font-s mb-3' : 't-font-m amenity-name')}>
                  {amenity.name}
                </span>
              </div>
            ))}
          </div>
          <div className={clsx(`component-content`, seeMore ? '' : 'hide-content')}>
            {amenitiesList?.slice(amenitiesCountLimit).map((amenity: any, index: number) => (
              <div
                className={clsx(
                  pageVal === 'chat' ? 'align-items-center' : '',
                  'col',
                  'm-icon-text',
                  'amenity-list-item'
                )}
                key={index}
              >
                <div
                  className={clsx(pageVal === 'chat' ? 'icon-s' : 'icon-m icon-decorative icon-holder', amenity.icon)}
                />
                <div className={clsx(pageVal === 'chat' ? 't-font-s mb-3' : 't-font-m amenity-name')}>
                  {amenity.name}
                </div>
              </div>
            ))}
          </div>
          {!seeMore && showMoreFlag && (
            <div
              ref={seeMoreButton}
              tabIndex={0}
              role="button"
              className={clsx(
                pageVal === 'chat' ? 'mb-0' : '',
                'see-amenities-button',
                'custom_click_track',
                !seeMore && showMoreFlag ? '' : 'hide-content'
              )}
              onClick={(): void => {
                setHasBeenClicked(true);
                setSeeMore(true);
              }}
              onKeyDown={(event): void => {
                if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
                  setHasBeenClicked(true);
                  setSeeMore(true);
                }
              }}
              {...{ custom_click_track_value: `${clickTrackingLoc}| See all amenities clicked |internal` }}
            >
              <div className={clsx(pageVal === 'chat' ? 't-subtitle-s' : 't-label-alt-xs', 'label-text')}>
                {jsonContent?.seeAllPropertyAmenitiesLabel}
              </div>
              <div className={clsx('icon-dropdown-down', 'label-icon')} />
            </div>
          )}
          {seeMore && showMoreFlag && (
            <div
              ref={seeFewerButton}
              tabIndex={0}
              className={clsx(
                pageVal === 'chat' ? 'mb-0' : '',
                'see-amenities-button',
                'custom_click_track',
                seeMore && showMoreFlag ? '' : 'hide-content'
              )}
              onClick={(): void => {
                setHasBeenClicked(true);
                setSeeMore(false);
              }}
              onKeyDown={(event): void => {
                if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
                  setHasBeenClicked(true);
                  setSeeMore(false);
                }
              }}
              {...{ custom_click_track_value: `${clickTrackingLoc}| See fewer amenities clicked |internal` }}
            >
              <div className={clsx(pageVal === 'chat' ? 't-subtitle-s' : 't-label-alt-xs', 'label-text')}>
                {jsonContent?.seeFewerAmenitiesLabel}
              </div>
              <div className={clsx('icon-dropdown-up', 'label-icon')} />
            </div>
          )}
        </div>
      </div>
    </Styled>
  ) : null;
};
